import { el } from "./dom.js";
import { api } from "../net/api.js";

export interface MenuActions {
  onLogout: () => void;
  onPractice: () => void;
  onTutorial: () => void;
  onPlay: () => void;
  onCosmetics: () => void;
  onMapEditor: () => void;
  onSettings: () => void;
  onProfile: () => void;
  onLeaderboard: () => void;
  onFriends: () => void;
  onAdmin: () => void;
}

/** Main menu after login.
 *
 *  Layout (2026-11 UX pass): ONE primary action (PLAY, captioned with what is
 *  behind it), two quieter rows of secondary destinations, a ghost nav row
 *  (profile / leaderboard / friends / settings), then the account action.
 *  Every entry carries a text label — icons only decorate. */
export function menuScreen(actions: MenuActions): HTMLElement {
  const screen = el("div", { class: "screen menu-hero" });
  const panel = el("div", { class: "panel menu-panel" });

  const logout = el("button", { class: "ghost wide danger-ghost", text: "Log Out", title: "Sign out of this account" });
  logout.onclick = async () => {
    logout.disabled = true; // feedback: the logout round-trip is not instant
    try { await api.logout(); } finally { actions.onLogout(); }
  };

  // PLAY = the one hero action. The caption says what is behind it, so a new
  // player knows "with friends" also starts here.
  const play = el("button", { class: "primary wide menu-play", "data-pad-start": "", title: "Play vs AI, online, or with friends" },
    el("span", { class: "menu-play-label", text: "Play" }),
    el("span", { class: "menu-play-sub", text: "vs AI · online · with friends" }));
  play.onclick = actions.onPlay;

  const practice = el("button", { class: "menu-sec", text: "🏁 Practice Range", title: "Practice Range: free practice on live targets — first kill starts the LADDER SPRINT clock; beat your best full-ladder time" });
  practice.onclick = actions.onPractice;
  const tutorial = el("button", { class: "menu-sec", text: "🎓 Training", title: "Step-by-step tutorial" });
  tutorial.onclick = actions.onTutorial;
  const cosmetics = el("button", { class: "menu-sec magenta", text: "🎨 Cosmetics", title: "Skins, colours and your custom avatar" });
  cosmetics.onclick = actions.onCosmetics;
  const mapEditor = el("button", { class: "menu-sec", text: "🛠 Build a Map", title: "Map editor: build, import from Minecraft, publish" });
  mapEditor.onclick = actions.onMapEditor;

  const settings = el("button", { class: "ghost", text: "Settings", title: "Controls, graphics, audio" });
  settings.onclick = actions.onSettings;

  // NO "Install the app" button here: the global top-bar "📲 INSTALL" button
  // (main.ts mkTopBtn) shows on every menu screen whenever the browser offers
  // the PWA prompt, so a second copy in this panel was pure duplication.

  const profile = el("button", { class: "ghost", text: "Profile", title: "Your stats and rank" });
  profile.onclick = actions.onProfile;
  const leaderboard = el("button", { class: "ghost", text: "Leaderboard", title: "Top players" });
  leaderboard.onclick = actions.onLeaderboard;
  const friendsBtn = el("button", { class: "ghost", text: "Friends", title: "Add friends, see who is online" });
  friendsBtn.onclick = actions.onFriends;
  // owner-only: the connection log panel
  const adminBtn = el("button", { class: "ghost", text: "👑 Admin", title: "Connection log (owner only)" });
  adminBtn.onclick = actions.onAdmin;
  const isAdmin = (api.account?.username ?? "").toLowerCase() === "supremechibre";

  if (api.guest) {
    logout.textContent = "Log In / Register";
    logout.title = "Create an account to keep your stats, rank and friends";
    logout.classList.remove("danger-ghost");
  }

  // who am I — one line under the logo, so the account state is always visible
  const who = el("div", { class: "menu-who" });
  if (api.guest) {
    who.append(el("span", { text: `Guest: ${api.account?.username ?? "guest"}` }), el("span", { class: "menu-who-note", text: " · progress is not saved" }));
  } else {
    who.append(el("span", { text: `Signed in as ${api.account?.username ?? "?"}` }));
    // PROGRESSION: fetch the rank badge and append it once it lands. Guests
    // have no persisted XP, so don't even ask.
    void api.getStatsAndRank().then(({ rank }) => {
      if (!rank) return;
      const badge = el("span", { class: "rank-chip", text: `${rank.name} ${rank.level}` });
      badge.style.color = rank.color;
      badge.style.borderColor = rank.color;
      who.append(" ", badge);
    }).catch(() => { /* menu must render even if stats are down */ });
  }

  // The build stamp used to be its own hint line here, duplicating the
  // always-on "v <sha>" corner label; it is the logo's tooltip now.
  panel.append(
    el("div", { class: "brand", text: "NEON PURGE", title: `build ${__BUILD_STAMP__}` }),
    el("div", { class: "brand-sub", text: "GUN GAME" }),
    who,
    play,
    el("div", { class: "row menu-pair" }, practice, tutorial),
    el("div", { class: "row menu-pair" }, cosmetics, mapEditor),
    // guests have no persistent stats — profile/friends are meaningless for them
    el("div", { class: "row menu-nav" }, ...(api.guest ? [leaderboard] : [profile, leaderboard, friendsBtn]), settings, ...(isAdmin ? [adminBtn] : [])),
    logout,
    el("div", { class: "hint pc-hint", text: "🎮 best experienced with a controller" }),
  );

  screen.append(panel);
  return screen;
}
